import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { listOrders, updateOrderStatus, clearAdminPassword } from '../utils/api';

const STATUS_COLORS = {
  pending: { bg: '#fff8e1', color: '#f59e0b' },
  confirmed: { bg: '#e8f5e9', color: '#2e7d32' },
  fulfilled: { bg: '#e3f2fd', color: '#1565c0' },
  cancelled: { bg: '#fce4ec', color: '#c62828' },
};

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [filterTeam, setFilterTeam] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [view, setView] = useState('orders');
  const navigate = useNavigate();

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await listOrders();
      setOrders(data.orders);
    } catch (err) {
      if (err.status === 401) { clearAdminPassword(); navigate('/'); return; }
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleStatusChange(orderId, status) {
    try {
      await updateOrderStatus(orderId, status);
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status } : o));
    } catch (err) {
      alert(`Failed: ${err.message}`);
    }
  }

  const teams = [...new Set(orders.map(o => o.team_name))].sort();

  const filtered = orders.filter(o => {
    if (filterTeam !== 'all' && o.team_name !== filterTeam) return false;
    if (filterStatus !== 'all' && o.status !== filterStatus) return false;
    return true;
  });

  const totalRevenue = filtered.reduce((sum, o) => sum + parseFloat(o.total_amount || 0), 0);

  // SKU summary
  const skuMap = {};
  filtered.forEach(order => {
    order.items.forEach(item => {
      if (!skuMap[item.sku]) {
        skuMap[item.sku] = {
          sku: item.sku,
          product: item.brand + ' ' + item.product_name,
          flex: item.flex,
          hand: item.hand,
          curve: item.blade_curve,
          total: 0,
          teams: {},
        };
      }
      skuMap[item.sku].total += item.quantity;
      if (!skuMap[item.sku].teams[order.team_name]) skuMap[item.sku].teams[order.team_name] = 0;
      skuMap[item.sku].teams[order.team_name] += item.quantity;
    });
  });
  const skuRows = Object.values(skuMap).sort((a, b) => b.total - a.total);

  return (
    <Layout title="Orders">
      <style>{css}</style>
      <div className="page-head">
        <h1 className="h1">Orders</h1>
        <div className="head-stats">
          <span className="stat">{filtered.length} orders</span>
          <span className="stat-divider">·</span>
          <span className="stat">${totalRevenue.toFixed(2)} total</span>
        </div>
      </div>

      <div className="filters">
        <div className="view-toggle">
          <button className={view === 'orders' ? 'view-btn active' : 'view-btn'} onClick={() => setView('orders')}>Orders</button>
          <button className={view === 'sku' ? 'view-btn active' : 'view-btn'} onClick={() => setView('sku')}>By SKU</button>
        </div>
        <select className="filter-select" value={filterTeam} onChange={e => setFilterTeam(e.target.value)}>
          <option value="all">All Teams</option>
          {teams.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        {view === 'orders' && (
          <select className="filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="fulfilled">Fulfilled</option>
            <option value="cancelled">Cancelled</option>
          </select>
        )}
      </div>

      {loading && <div className="card"><p className="muted">Loading...</p></div>}
      {error && <div className="card" style={{ borderLeft: '4px solid #d70015' }}><p>{error}</p></div>}

      {!loading && !error && view === 'sku' && (
        <div className="sku-table-wrap">
          {skuRows.length === 0
            ? <p className="muted">No orders yet.</p>
            : <table className="items-table">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Product</th>
                    <th>Flex</th>
                    <th>Hand</th>
                    <th>Curve</th>
                    <th>{filterTeam === 'all' ? 'All Teams Total' : filterTeam + ' Total'}</th>
                  </tr>
                </thead>
                <tbody>
                  {skuRows.map(row => (
                    <tr key={row.sku}>
                      <td className="mono">{row.sku}</td>
                      <td>{row.product}</td>
                      <td>{row.flex}</td>
                      <td>{row.hand}</td>
                      <td>{row.curve}</td>
                      <td><strong>{row.total}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
          }
        </div>
      )}

      {!loading && !error && view === 'orders' && filtered.length === 0 && (
        <div className="card"><p className="muted">No orders yet.</p></div>
      )}

      {!loading && !error && view === 'orders' && filtered.map(order => (
        <div key={order.id} className="order-card">
          <div className="order-header" onClick={() => setExpandedId(expandedId === order.id ? null : order.id)}>
            <div className="order-left">
              <div className="order-name">{order.first_name} {order.last_name}</div>
              <div className="order-meta">{order.team_name} · {order.email} {order.phone ? `· ${order.phone}` : ''}</div>
              <div className="order-date">{new Date(order.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
            </div>
            <div className="order-right">
              <div className="order-total">${parseFloat(order.total_amount).toFixed(2)}</div>
              <select
                className="status-select"
                value={order.status}
                style={{ background: STATUS_COLORS[order.status]?.bg, color: STATUS_COLORS[order.status]?.color }}
                onChange={e => { e.stopPropagation(); handleStatusChange(order.id, e.target.value); }}
                onClick={e => e.stopPropagation()}
              >
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="fulfilled">Fulfilled</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <span className="expand-icon">{expandedId === order.id ? '▲' : '▼'}</span>
            </div>
          </div>
          {expandedId === order.id && (
            <div className="order-items">
              <table className="items-table">
                <thead>
                  <tr>
                    <th>Product</th><th>SKU</th><th>Flex</th><th>Hand</th><th>Curve</th><th>Qty</th><th>Price</th><th>Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((item, i) => (
                    <tr key={i}>
                      <td>{item.brand} {item.product_name}</td>
                      <td className="mono">{item.sku}</td>
                      <td>{item.flex}</td>
                      <td>{item.hand}</td>
                      <td>{item.blade_curve}</td>
                      <td>{item.quantity}</td>
                      <td>${parseFloat(item.price_at_purchase).toFixed(2)}</td>
                      <td>${(parseFloat(item.price_at_purchase) * item.quantity).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </Layout>
  );
}

const css = `
  .page-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px; flex-wrap: wrap; gap: 12px; }
  .h1 { font-size: 28px; font-weight: 700; color: #1d1d1f; margin: 0; }
  .head-stats { display: flex; align-items: center; gap: 8px; }
  .stat { font-size: 14px; color: #6e6e73; font-weight: 500; }
  .stat-divider { color: #c0c0c8; }
  .filters { display: flex; gap: 12px; margin-bottom: 20px; flex-wrap: wrap; align-items: center; }
  .view-toggle { display: flex; border: 1.5px solid #e0e0e5; border-radius: 10px; overflow: hidden; }
  .view-btn { padding: 7px 18px; border: none; background: #fff; font-size: 13px; font-weight: 600; color: #6e6e73; cursor: pointer; font-family: inherit; transition: all 0.12s; }
  .view-btn.active { background: #1a1a2e; color: #fff; }
  .filter-select { padding: 8px 14px; border-radius: 10px; border: 1.5px solid #e0e0e5; font-size: 14px; font-family: inherit; color: #1d1d1f; background: #fff; cursor: pointer; outline: none; }
  .filter-select:focus { border-color: #1a1a2e; }
  .card { background: #fff; border-radius: 20px; box-shadow: 0 2px 20px rgba(0,0,0,0.06); padding: 32px; margin-bottom: 16px; }
  .muted { color: #86868b; font-size: 15px; margin: 0; }
  .sku-table-wrap { background: #fff; border-radius: 20px; box-shadow: 0 2px 20px rgba(0,0,0,0.06); padding: 24px; overflow-x: auto; }
  .order-card { background: #fff; border-radius: 20px; box-shadow: 0 2px 20px rgba(0,0,0,0.06); margin-bottom: 12px; overflow: hidden; }
  .order-header { display: flex; align-items: center; justify-content: space-between; padding: 18px 24px; cursor: pointer; gap: 16px; }
  .order-header:hover { background: #fafafa; }
  .order-left { flex: 1; min-width: 0; }
  .order-name { font-size: 15px; font-weight: 600; color: #1d1d1f; margin-bottom: 2px; }
  .order-meta { font-size: 13px; color: #6e6e73; margin-bottom: 2px; }
  .order-date { font-size: 12px; color: #aeaeb2; }
  .order-right { display: flex; align-items: center; gap: 12px; flex-shrink: 0; }
  .order-total { font-size: 17px; font-weight: 700; color: #1d1d1f; }
  .status-select { padding: 5px 10px; border-radius: 8px; border: none; font-size: 12px; font-weight: 600; cursor: pointer; font-family: inherit; outline: none; }
  .expand-icon { font-size: 11px; color: #aeaeb2; }
  .order-items { border-top: 1px solid #f0f0f5; padding: 0 24px 20px; overflow-x: auto; }
  .items-table { width: 100%; border-collapse: collapse; margin-top: 16px; }
  .items-table th { font-size: 11px; font-weight: 600; letter-spacing: 0.07em; text-transform: uppercase; color: #aeaeb2; padding: 0 12px 8px 0; text-align: left; white-space: nowrap; }
  .items-table td { font-size: 14px; color: #1d1d1f; padding: 8px 12px 8px 0; border-top: 1px solid #f5f5f7; }
  .mono { font-family: monospace; font-size: 13px; }
`;
